import { Checkbox, Spinner } from '@chakra-ui/react';
import { useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';

import FormInputLayout from '@/ui/molecules/FormInputLayout';
import PasswordInput from '@/ui/molecules/PasswordInput';
import {
  startLPSignUpFormInput,
  clickLPSignUpFormSubmit,
} from '@/utils/analytics/log-event';

import useHook from './hooks';
import { Props } from './props';

export const Component: React.FC<Props> = props => {
  const { submit, control, isSucceeded, isSubmitting } = useHook(props);

  const email = useWatch({ control, name: 'email' });
  const [sentFirstTouchEvent, setSentFirstTouchEvent] = useState(false);

  const logFirstInputChange = (inputType: string) => {
    if (!sentFirstTouchEvent) {
      setSentFirstTouchEvent(true);
      startLPSignUpFormInput(inputType);
    }
  };

  return (
    <>
      <div className="rounded-2xl border border-solid px-2 py-4 lg:p-10">
        {isSucceeded ? (
          <div className="rounded border border-blue-700 bg-blue-100 p-2 text-center text-blue-700">
            {email}に確認メールをお送りしました。
            <br />
            メールに記載の手順に沿って手続きを行ってください。
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="flex flex-col items-center gap-y-4">
              <div className="flex w-full gap-x-2 lg:gap-x-4">
                <div className="basis-1/2">
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field, fieldState: { error, isDirty } }) => (
                      <div onChange={() => logFirstInputChange('last_name')}>
                        <FormInputLayout
                          {...field}
                          label="姓"
                          placeholder="卓球"
                          type="text"
                          error={error}
                          isDirty={isDirty}
                        />
                      </div>
                    )}
                    rules={{
                      required: '姓を入力してください',
                    }}
                  />
                </div>
                <div className="basis-1/2">
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field, fieldState: { error, isDirty } }) => (
                      <div onChange={() => logFirstInputChange('first_name')}>
                        <FormInputLayout
                          {...field}
                          label="名"
                          placeholder="太郎"
                          type="text"
                          error={error}
                          isDirty={isDirty}
                        />
                      </div>
                    )}
                    rules={{
                      required: '名を入力してください',
                    }}
                  />
                </div>
              </div>
              <div className="w-full">
                <Controller
                  name="email"
                  control={control}
                  render={({ field, fieldState: { error, isDirty } }) => (
                    <div onChange={() => logFirstInputChange('email')}>
                      <FormInputLayout
                        {...field}
                        label="メールアドレス"
                        placeholder="t.takkyu@shands.jp"
                        type="email"
                        error={error}
                        isDirty={isDirty}
                      />
                    </div>
                  )}
                  rules={{
                    required: 'メールアドレスを入力してください',
                    maxLength: {
                      value: 60,
                      message: 'メールアドレスは60文字以内で入力してください。',
                    },
                  }}
                />
              </div>
              <div className="w-full">
                <Controller
                  name="password"
                  control={control}
                  render={({ field, fieldState: { error, isDirty } }) => (
                    <div onChange={() => logFirstInputChange('password')}>
                      <PasswordInput
                        {...field}
                        label="パスワード"
                        placeholder="******"
                        subtitle="英数字6文字以上"
                        error={error}
                        isDirty={isDirty}
                      />
                    </div>
                  )}
                  rules={{
                    required: 'パスワードを入力してください。',
                    minLength: {
                      value: 6,
                      message: 'パスワードは6文字以上で入力してください。',
                    },
                  }}
                />
              </div>
              <div className="mt-1 w-full">
                <Controller
                  control={control}
                  name="approveTermsOfUse"
                  render={({
                    field: { onBlur, onChange, value },
                    fieldState: { error },
                  }) => (
                    <div
                      onClick={() =>
                        logFirstInputChange('approve_terms_of_use')
                      }
                    >
                      <div>
                        <a
                          href="https://my.shands.jp/member_rules"
                          target="_blank"
                          className="underline underline-offset-4"
                        >
                          会員規約
                        </a>
                        への同意
                      </div>
                      <div className="mt-2 flex h-12 w-full rounded border-2 border-gray-100 bg-white">
                        <Checkbox
                          onBlur={onBlur}
                          onChange={event => onChange(event.target.checked)}
                          checked={value}
                          className="h-full w-full pl-4"
                        >
                          会員規約に同意します。
                        </Checkbox>
                      </div>
                      {error && (
                        <div className="mt-1.5 text-xs text-red-500">
                          {error.message}
                        </div>
                      )}
                    </div>
                  )}
                  rules={{
                    required: '会員規約への同意は必須です。',
                  }}
                />
              </div>
              <div className="flex h-12 w-56 items-center justify-center rounded bg-blue-900 font-bold text-white lg:mt-4">
                {isSubmitting ? (
                  <button
                    disabled
                    className="flex h-full w-full items-center justify-center rounded bg-gray-300"
                  >
                    <Spinner />
                  </button>
                ) : (
                  <input
                    type="submit"
                    value="次へ"
                    className="h-full w-full"
                    onClick={clickLPSignUpFormSubmit}
                  />
                )}
              </div>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default Component;
